import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import { newBigIntId } from 'src/utils/idUtils';
import {
  MarketplacePaymentLineType,
  MarketplacePaymentState,
  SaleCostType,
  SaleLineItem,
} from 'src/WebApiController';

import { RetailSaleFormValues, RetailSalePaymentLine } from '../types';

const roundTo2 = (n: number) => Math.round(n * 100) / 100;

export const useRetailLineItemOperations = () => {
  const { loginContext } = useAppContext();
  const { setValue, watch, getValues } = useFormContext<RetailSaleFormValues>();

  const marketplacePaymentLines = watch('marketplacePaymentLines');
  const totalNetProceeds = watch('totalNetProceeds');
  const currencyCode =
    watch('currencyCode') ??
    loginContext?.user?.activeAccount.currencyCode ??
    'USD';

  const [isLineItemModalOpen, setIsLineItemModalOpen] = useState(false);

  const openLineItemModal = useCallback(() => setIsLineItemModalOpen(true), []);
  const closeLineItemModal = useCallback(
    () => setIsLineItemModalOpen(false),
    []
  );

  const updateLineItem = useCallback(
    (lineId: number | null, updates: Partial<RetailSalePaymentLine>) => {
      const current = getValues('marketplacePaymentLines') ?? [];
      setValue(
        'marketplacePaymentLines',
        current.map((l) =>
          l.marketplacePaymentLineId === lineId ? { ...l, ...updates } : l
        )
      );
    },
    [getValues, setValue]
  );

  const onLineAmountChange = useCallback(
    (line: RetailSalePaymentLine, absAmount: number) => {
      const signedAmount = line.isCredit ? absAmount : -absAmount;
      const rate = totalNetProceeds
        ? roundTo2((signedAmount / totalNetProceeds) * 100)
        : null;
      updateLineItem(line.marketplacePaymentLineId, {
        amount: signedAmount,
        ratePercent: rate,
      });
    },
    [totalNetProceeds, updateLineItem]
  );

  const onLineRateChange = useCallback(
    (line: RetailSalePaymentLine, rate: number | null) => {
      if (rate === null) {
        updateLineItem(line.marketplacePaymentLineId, { ratePercent: null });
        return;
      }
      const newAmount = totalNetProceeds
        ? roundTo2((rate / 100) * totalNetProceeds)
        : line.amount;
      updateLineItem(line.marketplacePaymentLineId, {
        ratePercent: rate,
        amount: newAmount,
      });
    },
    [totalNetProceeds, updateLineItem]
  );

  const onSaveLineItem = useCallback(
    (item: SaleLineItem) => {
      const isCredit = item.costType === SaleCostType.Credit;
      const signedAmount = isCredit
        ? -Math.abs(item.cost)
        : Math.abs(item.cost);
      const rate = totalNetProceeds
        ? roundTo2((signedAmount / totalNetProceeds) * 100)
        : null;
      const newLine: RetailSalePaymentLine = {
        marketplacePaymentLineId: item.id || newBigIntId(),
        marketplacePaymentId: null,
        externalPaymentId: item.paymentReferenceId ?? '',
        externalPaymentLineId: `paymentline-${Date.now()}`,
        externalSaleId: '',
        saleId: null,
        currency: item.currencyCode,
        amount: signedAmount,
        isCredit: isCredit,
        paymentLineType: isCredit
          ? MarketplacePaymentLineType.Credit
          : MarketplacePaymentLineType.Charge,
        paymentState: item.paymentReceived
          ? MarketplacePaymentState.Paid
          : null,
        paymentDate: item.costDate,
        receivedDate: null,
        isLocked: item.isMarketplacePaymentLocked,
        ratePercent: rate,
        saleLineItemChargeReason: item.saleLineItemChargeReason ?? null,
        saleLineItemCreditReason: item.saleLineItemCreditReason ?? null,
        description: item.description || (isCredit ? 'Credit' : 'Charge'),
        externalPaymentMethodType: null,
        externalPaymentNote: null,
      };
      const current = getValues('marketplacePaymentLines') ?? [];
      setValue('marketplacePaymentLines', [...current, newLine]);
      closeLineItemModal();
    },
    [closeLineItemModal, getValues, setValue, totalNetProceeds]
  );

  const onDeleteLineItem = useCallback(
    (lineId: number | null) => {
      const current = getValues('marketplacePaymentLines') ?? [];
      setValue(
        'marketplacePaymentLines',
        current.filter((l) => l.marketplacePaymentLineId !== lineId)
      );
    },
    [getValues, setValue]
  );

  const totalAmount = useMemo(() => {
    return (
      (totalNetProceeds ?? 0) +
      (marketplacePaymentLines ?? []).reduce(
        (sum, l) => sum + (l.amount ?? 0),
        0
      )
    );
  }, [marketplacePaymentLines, totalNetProceeds]);

  return {
    marketplacePaymentLines,
    totalNetProceeds,
    currencyCode,
    isLineItemModalOpen,
    openLineItemModal,
    closeLineItemModal,
    onLineAmountChange,
    onLineRateChange,
    onSaveLineItem,
    onDeleteLineItem,
    totalAmount,
  };
};
